/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
  type ComponentRef,
  type CSSProperties,
  type ReactNode,
} from "react";

import { IconButton } from "../../atoms";
import { useBreakpoint } from "../../hooks";
import { cn } from "../../lib";
import { Box } from "../../primitives";
import { Sheet } from "./sheet";
import type { SidebarProviderProps, UseSidebar } from "./types";

/**
 * sidebar.tsx — vendored shadcn `sidebar` block, re-tokenized to `luce-*`.
 *
 * This is **copied source** (shadcn's composite sidebar block), NOT a thin
 * primitive wrapper — there is no `@radix-ui/react-sidebar`. It is INTERNAL to
 * `app-sidebar/`: only `app-sidebar.tsx` consumes these parts; nothing here is
 * exported from `@luce/ui`, and none carry a `Primitive` suffix.
 *
 * What is preserved verbatim from shadcn (do NOT touch in review):
 * - `SidebarProvider` state machine: cookie persistence (`SIDEBAR_COOKIE_NAME`),
 *   `Cmd/Ctrl+B` keyboard shortcut, `useIsMobile` off-canvas switch.
 * - The `data-state` / `data-collapsible` / `data-side` / `data-variant`
 *   attributes that drive the CSS width transition.
 *
 * What changed:
 * - Every `bg-sidebar` / `text-sidebar-foreground` / `border-sidebar-*` class is
 *   re-mapped to a Luce token.
 * - The mobile branch imports the co-located sibling `./sheet` (which composes
 *   our `dialog` primitive) instead of vendoring shadcn's own `sheet.tsx` —
 *   this is the one structural edit beyond re-tokenizing.
 * - Trimmed to the parts `app-sidebar.tsx` actually composes: provider, the
 *   `Sidebar` shell, header / content / footer regions, rail, and trigger. The
 *   shadcn menu parts (`SidebarMenu` / `SidebarMenuButton` / `SidebarGroup`) are
 *   intentionally absent — our Figma `SidebarMenu` organism replaces them.
 *
 * There is no `sidebar.native.tsx`: `app-sidebar.native.tsx` never imports this
 * file, so Metro never resolves the block on native. It is web-only by design.
 *
 * @see docs/tickets/DS-042-sidebar-menu.md
 */

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";

/** Allow the `--sidebar-width*` custom properties on inline `style`. */
type SidebarCSSProperties = CSSProperties & Record<`--${string}`, string | number>;

const SidebarContext = createContext<UseSidebar | null>(null);

/** Read the shell's live collapse / mobile state. */
export function useSidebar(): UseSidebar {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }
  return context;
}

/**
 * Owns all sidebar state: controlled/uncontrolled `open`, cookie persistence,
 * the `Cmd/Ctrl+B` shortcut, and the mobile off-canvas flag. Wraps the app
 * subtree (sidebar + page content) so `useSidebar()` is available to both.
 */
export const SidebarProvider = forwardRef<
  ComponentRef<typeof Box>,
  SidebarProviderProps & { style?: CSSProperties; children?: ReactNode }
>(
  (
    { defaultOpen = true, open: openProp, onOpenChange, className, style, children, ...props },
    ref,
  ) => {
    const isMobile = useBreakpoint() === "sm";
    const [openMobile, setOpenMobile] = useState(false);

    // Uncontrolled internal state; `openProp` takes over when provided.
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const open = openProp ?? internalOpen;

    const setOpen = useCallback(
      (value: boolean | ((value: boolean) => boolean)) => {
        const next = typeof value === "function" ? value(open) : value;
        if (onOpenChange) {
          onOpenChange(next);
        } else {
          setInternalOpen(next);
        }
        // Persist collapse across reloads (7-day cookie), matching shadcn.
        document.cookie = `${SIDEBAR_COOKIE_NAME}=${next}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
      },
      [onOpenChange, open],
    );

    const toggleSidebar = useCallback(() => {
      return isMobile ? setOpenMobile((value) => !value) : setOpen((value) => !value);
    }, [isMobile, setOpen]);

    useEffect(() => {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          toggleSidebar();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [toggleSidebar]);

    const state = open ? "expanded" : "collapsed";

    const contextValue = useMemo<UseSidebar>(
      () => ({
        state,
        open,
        setOpen,
        isMobile,
        openMobile,
        setOpenMobile,
        toggleSidebar,
      }),
      [state, open, setOpen, isMobile, openMobile, toggleSidebar],
    );

    return (
      <SidebarContext.Provider value={contextValue}>
        <Box
          ref={ref}
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              "--sidebar-width-mobile": SIDEBAR_WIDTH_MOBILE,
              ...style,
            } as SidebarCSSProperties
          }
          className={cn(
            "group/sidebar-wrapper text-luce-neutral-foreground-default flex min-h-svh w-full",
            className,
          )}
          {...props}
        >
          {children}
        </Box>
      </SidebarContext.Provider>
    );
  },
);
SidebarProvider.displayName = "SidebarProvider";

interface SidebarShellProps extends ComponentProps<typeof Box> {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
}

/**
 * The rail shell. On mobile it renders an off-canvas `Sheet`; on desktop a
 * fixed column whose width animates between `--sidebar-width` and
 * `--sidebar-width-icon` off the `data-collapsible` attribute.
 */
export const Sidebar = forwardRef<ComponentRef<typeof Box>, SidebarShellProps>(
  (
    {
      side = "left",
      variant = "sidebar",
      collapsible = "offcanvas",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

    if (collapsible === "none") {
      return (
        <Box
          ref={ref}
          className={cn(
            "bg-luce-neutral-background-default flex h-full w-[--sidebar-width] flex-col",
            className,
          )}
          {...props}
        >
          {children}
        </Box>
      );
    }

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile} side={side}>
          {children}
        </Sheet>
      );
    }

    return (
      <Box
        ref={ref}
        className="text-luce-neutral-foreground-default group peer hidden md:block"
        data-state={state}
        data-collapsible={state === "collapsed" ? collapsible : ""}
        data-variant={variant}
        data-side={side}
      >
        {/* Spacer that reserves the rail's width in the page flow. */}
        <Box
          className={cn(
            "relative h-svh w-[--sidebar-width] bg-transparent transition-[width] duration-200 ease-linear",
            "group-data-[collapsible=offcanvas]:w-0",
            "group-data-[side=right]:rotate-180",
            variant === "floating" || variant === "inset"
              ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))]"
              : "group-data-[collapsible=icon]:w-[--sidebar-width-icon]",
          )}
        />
        <Box
          className={cn(
            "fixed inset-y-0 z-10 hidden h-svh w-[--sidebar-width] transition-[left,right,width] duration-200 ease-linear md:flex",
            side === "left"
              ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
              : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
            variant === "floating" || variant === "inset"
              ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)]"
              : "border-luce-neutral-border-mediumEmphasis group-data-[collapsible=icon]:w-[--sidebar-width-icon] group-data-[side=left]:border-r group-data-[side=right]:border-l",
            className,
          )}
          {...props}
        >
          <Box
            data-sidebar="sidebar"
            className={cn(
              "bg-luce-neutral-background-default flex h-full w-full flex-col",
              "group-data-[variant=floating]:border-luce-neutral-border-mediumEmphasis group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:shadow",
            )}
          >
            {children}
          </Box>
        </Box>
      </Box>
    );
  },
);
Sidebar.displayName = "Sidebar";

/** Toggle button — collapses the icon rail on desktop, opens the Sheet on mobile. */
export const SidebarTrigger = forwardRef<
  ComponentRef<typeof IconButton>,
  // Its own "Toggle Sidebar" label is the default, so a caller needn't pass one
  Omit<ComponentProps<typeof IconButton>, "icon" | "accessibilityLabel"> & {
    accessibilityLabel?: string;
  }
>(({ onClick, ...props }, ref) => {
  const { toggleSidebar, isMobile } = useSidebar();
  return (
    <IconButton
      ref={ref}
      icon={isMobile ? "List" : "SidebarSimple"}
      variant="neutral"
      size="md"
      accessibilityLabel="Toggle Sidebar"
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...props}
    />
  );
});
SidebarTrigger.displayName = "SidebarTrigger";

/** Thin hit-target along the rail edge — click to toggle collapse. */
export const SidebarRail = forwardRef<HTMLButtonElement, ComponentProps<"button">>(
  ({ className, ...props }, ref) => {
    const { toggleSidebar } = useSidebar();
    return (
      <button
        ref={ref}
        type="button"
        aria-label="Toggle Sidebar"
        tabIndex={-1}
        onClick={toggleSidebar}
        title="Toggle Sidebar"
        className={cn(
          "hover:after:bg-luce-neutral-border-mediumEmphasis absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex",
          "group-data-[side=left]:-right-4 group-data-[side=right]:left-0",
          "[[data-side=left]_&]:cursor-w-resize [[data-side=right]_&]:cursor-e-resize",
          // A mouse affordance. On touch screens it only catches taps meant for
          // the collapsed chips and the page beside it; the trigger stays
          "[@media(pointer:coarse)]:hidden",
          className,
        )}
        {...props}
      />
    );
  },
);
SidebarRail.displayName = "SidebarRail";

/** Top chrome region (logo / workspace switcher). */
export const SidebarHeader = forwardRef<ComponentRef<typeof Box>, ComponentProps<typeof Box>>(
  ({ className, ...props }, ref) => (
    <Box
      ref={ref}
      data-sidebar="header"
      className={cn("gap-spacing-sm p-spacing-md flex flex-col", className)}
      {...props}
    />
  ),
);
SidebarHeader.displayName = "SidebarHeader";

/** Bottom chrome region (user menu / sign-out). */
export const SidebarFooter = forwardRef<ComponentRef<typeof Box>, ComponentProps<typeof Box>>(
  ({ className, ...props }, ref) => (
    <Box
      ref={ref}
      data-sidebar="footer"
      className={cn("gap-spacing-sm p-spacing-md flex flex-col", className)}
      {...props}
    />
  ),
);
SidebarFooter.displayName = "SidebarFooter";

/** Scrollable middle region that hosts the `SidebarMenu`. */
export const SidebarContent = forwardRef<ComponentRef<typeof Box>, ComponentProps<typeof Box>>(
  ({ className, ...props }, ref) => (
    <Box
      ref={ref}
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-auto group-data-[collapsible=icon]:overflow-hidden",
        className,
      )}
      {...props}
    />
  ),
);
SidebarContent.displayName = "SidebarContent";
